import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { Unauthorized } from "./pages/Unauthorized";
import { SetupPassword } from "./pages/auth/SetupPassword";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

import { ListaPlanos } from "./pages/planos/ListaPlanos";
import { NovoPlano } from "./pages/planos/NovoPlano";
import { EditarPlano } from "./pages/planos/EditarPlano";

import { ListaIncorporadoras } from "./pages/incorporadoras/ListaIncorporadoras";
import { NovaIncorporadora } from "./pages/incorporadoras/NovaIncorporadora";
import { DetalhesIncorporadora } from "./pages/incorporadoras/DetalhesIncorporadora";
import { EditarIncorporadora } from "./pages/incorporadoras/EditarIncorporadora";

import { ListaFranquias } from "./pages/franquias/ListaFranquias";
import { NovoFranquia } from "./pages/franquias/NovoFranquia";
import { EditarFranquia } from "./pages/franquias/EditarFranquia";
import { EditarDetalhesFranquia } from "./pages/franquias/EditarDetalhesFranquia";
import { GerenciarAreasAtuacao } from "./pages/franquias/GerenciarAreasAtuacao";
import { DetalhesFranquia } from "./pages/franquias/DetalhesFranquia";
import { GerenciarCodigos } from "./pages/franquias/GerenciarCodigos";
import { DetalhesPedido } from "./pages/franquias/DetalhesPedido";

import { ListaAssinantes as ListaFuncionarios } from "./pages/funcionarios/ListaAssinantes";
import { Novofuncionario } from "./pages/funcionarios/Novofuncionario";
import { DetalhesFuncionario } from "./pages/funcionarios/DetalhesFuncionario";
import { Financeiro } from "./pages/funcionarios/Financeiro";

import { ListaAssinantes as ListaAssinantesPage } from "./pages/assinantes/ListaAssinantes";
import { NovoAssinante } from "./pages/assinantes/NovoAssinante";
import { DetalhesAssinante } from "./pages/assinantes/DetalhesAssinante";
import { EditarAssinante } from "./pages/assinantes/EditarAssinante";

import { Listavincular } from "./pages/assinaturas/Listavincular";
import { NovaAssinatura } from "./pages/assinaturas/NovaAssinatura";
import { EditarAssinatura } from "./pages/assinaturas/EditarAssinatura";

import ListaMovimentacoes from "./pages/movimentacoes/ListaMovimentacoes";
import NovaMovimentacao from "./pages/movimentacoes/NovaMovimentacao";
import { ConfiguracoesPerfil } from "./pages/ConfiguracoesPerfil";

import ListaEmpreendimentos from "./pages/empreendimentos/ListaEmpreendimentos";
import NovoEmpreendimento from "./pages/empreendimentos/NovoEmpreendimento";
import EditarEmpreendimento from "./pages/empreendimentos/EditarEmpreendimento";
import VisualizarEmpreendimento from "./pages/empreendimentos/VisualizarEmpreendimento";
import TesteEmpreendimento from "./pages/empreendimentos/TesteEmpreendimento";

import { Sidebar } from "./components/Sidebar";
import "./styles/colors.css";

import DashFinanceiro from "./pages/financeiro/DashFinanceiro";
import RelatoriosFinanceiros from "./pages/financeiro/RelatoriosFinanceiros";

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading, logout } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      if (
        e.key === "PrintScreen" ||
        (e.ctrlKey &&
          (e.key.toLowerCase() === "p" || e.key.toLowerCase() === "s")) ||
        (e.ctrlKey &&
          e.shiftKey &&
          ["S", "I", "J", "C"].includes(e.key.toUpperCase())) ||
        (isMac &&
          e.metaKey &&
          (e.key.toLowerCase() === "p" || e.key.toLowerCase() === "s"))
      ) {
        e.preventDefault();
        alert(
          "Captura de tela e atalhos de impressão estão desativados nesta aplicação."
        );
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#29306a] to-[#32bef0]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }

  // Se estiver autenticado e na página de login, redireciona para a home
  if (isAuthenticated && location.pathname === "/login") {
    return <Navigate to="/empreendimentos" replace />;
  }

  // Se não estiver autenticado e tentando acessar uma rota protegida, redireciona para o login
  if (
    !isAuthenticated &&
    location.pathname !== "/login" &&
    location.pathname !== "/register"
  ) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-[#f8faff] relative">
      {isAuthenticated ? (
        <div className="flex">
          <Sidebar onLogout={logout} />
          <main className="flex-1 p-4 lg:p-8 ml-64 lg:ml-64 md:ml-0 sm:ml-0 min-h-screen w-full">
            <Routes>
              <Route path="/" element={<Navigate to="/empreendimentos" replace />} />
              <Route path="/planos" element={<ListaPlanos />} />
              <Route path="/planos/novo" element={<NovoPlano />} />
              <Route path="/planos/:id/editar" element={<EditarPlano />} />

            <Route
              path="/financeiro/franqueado/dashboard"
              element={<DashFinanceiro />}
            />

            <Route
              path="/financeiro/franqueado/relatorios"
              element={<RelatoriosFinanceiros />}
            />

              <Route path="/incorporadoras" element={<ListaIncorporadoras />} />
              <Route
                path="/incorporadoras/novo"
                element={<NovaIncorporadora />}
              />
              <Route
                path="/incorporadoras/:id"
                element={<DetalhesIncorporadora />}
              />
              <Route
                path="/incorporadoras/:id/editar"
                element={<EditarIncorporadora />}
              />

              <Route path="/franquias" element={<ListaFranquias />} />
              <Route path="/franquias/novo" element={<NovoFranquia />} />
              <Route
                path="/franquias/:id/editar"
                element={<EditarFranquia />}
              />
              <Route
                path="/franquias/:id/editar-detalhes"
                element={<EditarDetalhesFranquia />}
              />
              <Route
                path="/franquias/:id/areas-atuacao"
                element={<GerenciarAreasAtuacao />}
              />
              <Route
                path="/franquias/:id/detalhes"
                element={<DetalhesFranquia />}
              />
              <Route path="/franquias/pedidos" element={<GerenciarCodigos />} />
              <Route
                path="/franquias/pedidos/:id"
                element={<DetalhesPedido />}
              />

              <Route path="/funcionarios" element={<ListaFuncionarios />} />
              <Route path="/funcionarios/novo" element={<Novofuncionario />} />
              <Route
                path="/funcionarios/:id/detalhes"
                element={<DetalhesFuncionario />}
              />

              <Route
                path="/funcionarios/:id/financeiro"
                element={<Financeiro />}
              />

              <Route path="/assinantes" element={<ListaAssinantesPage />} />
              <Route path="/assinantes/novo" element={<NovoAssinante />} />
              <Route
                path="/assinantes/:id/detalhes"
                element={<DetalhesAssinante />}
              />
              <Route
                path="/assinantes/:id/editar"
                element={<EditarAssinante />}
              />

              <Route
                path="/assinantes/:id/financeiro"
                element={<Financeiro />}
              />

              <Route path="/Listavincular" element={<Listavincular />} />
              <Route path="/assinaturas/novo" element={<NovaAssinatura />} />
              <Route
                path="/assinaturas/:id/editar"
                element={<EditarAssinatura />}
              />

              <Route
                path="/empreendimentos"
                element={<ListaEmpreendimentos />}
              />
              <Route
                path="/empreendimentos/novo"
                element={<NovoEmpreendimento />}
              />
              <Route
                path="/empreendimentos/editar/:id"
                element={<EditarEmpreendimento />}
              />
              <Route
                path="/empreendimentos/:id/visualizar"
                element={<VisualizarEmpreendimento />}
              />
              <Route
                path="/empreendimentos/teste"
                element={<TesteEmpreendimento />}
              />

              <Route path="/movimentacoes" element={<ListaMovimentacoes />} />
              <Route
                path="/movimentacoes/nova"
                element={<NovaMovimentacao />}
              />

              <Route path="/configuracoes" element={<ConfiguracoesPerfil />} />

              <Route path="*" element={<Navigate to="/empreendimentos" replace />} />
            </Routes>
          </main>
        </div>
      ) : (
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/crie-sua-senha" element={<SetupPassword />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      )}
    </div>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
        {/* react-toastify já era usado em alguns formulários (toast.success/
            toast.error), mas sem este container montado em lugar nenhum do
            app essas chamadas nunca apareciam na tela — eram no-ops. */}
        <ToastContainer
          position="top-right"
          autoClose={5000}
          closeOnClick
          pauseOnHover
          newestOnTop
        />
      </AuthProvider>
    </Router>
  );
}

export default App;
